// ================================================================
// 問題詳情頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：顯示問題詳細資訊、確認查詢、餘額檢查、提交分析請求
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useAuth } from '../hooks/useAuth';
import {
  getQuestionDetail,
  getRecommendedQuestions,
  getCategoryColor,
  getCategoryName,
  getDynamicQuestionTitle,
  getDynamicDescription,
  Question
} from '../api/questions';
import { askQuestion, getMonthlyFreeStatus } from '../api/qa';
import { checkBalanceSufficient, getBalanceStatus } from '../api/qa';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * 檢查問題是否免費（處理數字/布爾值）
 */
const isQuestionFree = (isFree: boolean | number | undefined): boolean => {
  if (typeof isFree === 'boolean') return isFree;
  if (typeof isFree === 'number') return isFree === 1;
  return false;
};

/**
 * 問題詳情頁面組件
 */
const QuestionDetailPage: React.FC = () => {
  const { question_id } = useParams<{ question_id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();

  // 狀態
  const [question, setQuestion] = useState<Question | null>(null);
  const [recommendations, setRecommendations] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // 確認對話框
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  // 每月免費問題狀態
  const [monthlyFreeAvailable, setMonthlyFreeAvailable] = useState<boolean>(false);

  // 載入問題詳情
  const loadQuestionDetail = useCallback(async () => {
    if (!question_id) return;

    setLoading(true);
    setError('');

    try {
      const result = await getQuestionDetail(question_id);
      console.log('Question detail API response:', result);

      if (result.success && result.question) {
        console.log('Question data:', result.question);
        setQuestion(result.question);
      } else {
        setError('問題不存在');
      }
    } catch (err: any) {
      console.error('Failed to load question:', err);
      setError(err.message || '載入問題失敗');
    } finally {
      setLoading(false);
    }
  }, [question_id]);

  // 載入推薦問題
  const loadRecommendations = useCallback(async () => {
    if (!question_id) return;

    try {
      const result = await getRecommendedQuestions(question_id);

      if (result.success) {
        setRecommendations(result.recommendations);
      }
    } catch (err) {
      // 推薦問題失敗不影響主要功能
      console.error('載入推薦問題失敗:', err);
    }
  }, [question_id]);

  // 自動載入問題詳情
  useEffect(() => {
    if (question_id) {
      loadQuestionDetail();
      loadRecommendations();
    }
  }, [question_id, loadQuestionDetail, loadRecommendations]);

  // 載入每月免費狀態（登入後且問題已載入時查詢）
  useEffect(() => {
    if (user && question && ['MAIN', 'COMMON'].includes(question.category_id)) {
      getMonthlyFreeStatus().then(res => {
        if (res.success) setMonthlyFreeAvailable(res.monthly_free_available);
      });
    }
  }, [user, question]);

  // 檢查是否可以查詢
  const canQuery = (): { allowed: boolean; message: string } => {
    if (!user) {
      return { allowed: false, message: '請先登入' };
    }

    if (!question) {
      return { allowed: false, message: '問題資料載入中' };
    }

    // 檢查餘額（包含點數；若有每月免費可用則略過）
    if (!isQuestionFree(question.is_free) && !monthlyFreeAvailable) {
      const price = question.price || 0;
      const bonusPoints = user.bonus_points || 0;
      const balance = user.balance || 0;
      const debtLimit = user.debt_limit || 500;
      const availableTotal = bonusPoints + balance + debtLimit;

      if (availableTotal < price) {
        return {
          allowed: false,
          message: `餘額不足，需要 ${formatPrice(price)}，目前可用總額 ${formatPrice(availableTotal)}`
        };
      }
    }

    // 檢查是否為 PAIR 類別（需要雙人資料）
    if (question.category_id === 'PAIR') {
      return {
        allowed: false,
        message: '此為配對分析問題，需要輸入雙人資料。請點擊導航欄的「配對分析」(❤️) 按鈕進行查詢。'
      };
    }

    // 檢查是否為需要雙人資料的問題（SIMPLE001、MAIN009）
    if (question.question_id === 'SIMPLE001' || question.question_id === 'MAIN009') {
      return {
        allowed: false,
        message: '此問題需要雙人資料進行配對分析。請點擊下方「前往配對分析」按鈕。'
      };
    }

    return { allowed: true, message: '' };
  };

  // 處理查詢請求
  const handleQuery = async () => {
    if (!user || !question) return;

    // 驗證必要參數
    if (!user.birth_time || !user.gender) {
      setError('使用者資料不完整，缺少出生時間或性別資訊');
      console.error('Missing user data:', { birth_time: user.birth_time, gender: user.gender });
      return;
    }

    if (!question.question_id) {
      setError('問題資料不完整');
      console.error('Missing question_id:', question);
      return;
    }

    setProcessing(true);
    setError('');

    console.log('Submitting query:', {
      question_id: question.question_id,
      birth_time: user.birth_time,
      gender: user.gender
    });

    try {
      const result = await askQuestion({
        question_id: question.question_id,
        birth_time: user.birth_time,
        gender: user.gender
      });

      if (result.success) {
        // 導航到答案頁面
        navigate('/answer', {
          state: {
            question: question,
            answer: result
          }
        });
      } else {
        setError(result.error || '查詢失敗');
      }
    } catch (err: any) {
      setError(err.message || '查詢失敗');
    } finally {
      setProcessing(false);
      setConfirmOpen(false);
    }
  };

  // 開啟確認對話框
  const openConfirmDialog = () => {
    const check = canQuery();

    if (!check.allowed) {
      setError(check.message);
      return;
    }

    setConfirmOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !question) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/questions')} sx={{ mt: 2 }}>
          返回問題列表
        </Button>
      </Container>
    );
  }

  if (!question) {
    return null;
  }

  // 餘額狀態
  const balanceStatus = user ? getBalanceStatus(user.balance) : null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* 返回按鈕 */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/questions')}
        sx={{ mb: 2 }}
      >
        返回問題列表
      </Button>

      {/* 問題詳情 */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        {/* 類別標籤 */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label={getCategoryName(question.category_id)}
            sx={{
              backgroundColor: getCategoryColor(question.category_id),
              color: 'white',
              fontWeight: 'bold'
            }}
          />

          {isQuestionFree(question.is_free) && (
            <Chip label="免費" color="success" />
          )}

          {!isQuestionFree(question.is_free) && monthlyFreeAvailable && (
            <Chip label="本月免費" color="success" variant="outlined" />
          )}

          {question.requires_matching && (
            <Chip label="需要配對" color="warning" />
          )}
        </Box>

        {/* 問題標題 */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          問題: {getDynamicQuestionTitle(question)}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* 問題描述 */}
        {question.description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" paragraph>
              {getDynamicDescription(question)}
            </Typography>
          </Box>
        )}

        {/* 動態時間範圍資訊（2026-02-02 新增）*/}
        {question.time_range_start && question.time_range_end && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>分析時間範圍：</strong>
              {question.time_range_start} ~ {question.time_range_end}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              系統已根據當前日期自動計算{question.period_label ? `（${question.period_label}）` : ''}
            </Typography>
          </Alert>
        )}

        {/* 價格與餘額資訊 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  查詢費用
                </Typography>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                  {isQuestionFree(question.is_free)
                    ? '免費'
                    : monthlyFreeAvailable
                      ? '本月免費'
                      : formatPrice(question.price || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {user && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    帳戶餘額
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: balanceStatus?.color }}>
                    {formatPrice((user.bonus_points || 0) + (user.balance || 0))}
                  </Typography>
                  {user.bonus_points && user.bonus_points > 0 && (
                    <Typography variant="caption" display="block" color="primary">
                      💎 體驗點數: {user.bonus_points} 點
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ color: balanceStatus?.color }}>
                    💰 現金餘額: {formatPrice(user.balance || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 查詢按鈕 */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {question.category_id === 'PAIR' || question.question_id === 'SIMPLE001' || question.question_id === 'MAIN009' ? (
            // PAIR 類別或 SIMPLE001：引導到配對分析頁面，傳遞 question_id
            <Button
              variant="contained"
              size="large"
              color="secondary"
              onClick={() => {
                console.log('Button clicked! Navigating to /pair with state:', {
                  question_id: question.question_id,
                  question_text: getDynamicQuestionTitle(question),
                  price: question.price
                });
                // 儲存問題資訊到 sessionStorage，供配對分析頁面讀取
                sessionStorage.setItem('pairAnalysisQuestion', JSON.stringify({
                  question_id: question.question_id,
                  question_text: getDynamicQuestionTitle(question),
                  price: question.price
                }));
                // 使用 window.location 確保頁面跳轉
                window.location.href = '/pair';
              }}
              startIcon={<FavoriteIcon />}
            >
              前往配對分析頁面
            </Button>
          ) : (
            // 其他類別：正常查詢
            <Button
              variant="contained"
              size="large"
              onClick={openConfirmDialog}
              disabled={processing || !user}
              startIcon={processing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {processing ? '處理中...' : '立即查詢'}
            </Button>
          )}

          {!user && question.category_id !== 'PAIR' && question.question_id !== 'SIMPLE001' && question.question_id !== 'MAIN009' && (
            <Alert severity="warning" sx={{ flexGrow: 1 }}>
              請先登入才能查詢
            </Alert>
          )}

          {(question.category_id === 'PAIR' || question.question_id === 'SIMPLE001' || question.question_id === 'MAIN009') && (
            <Alert severity="info" sx={{ flexGrow: 1 }}>
              此問題需要輸入雙人資料進行配對分析，請使用配對分析專用頁面
            </Alert>
          )}
        </Box>
      </Paper>

      {/* 推薦問題 */}
      {recommendations.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            相關推薦問題
          </Typography>

          <Grid container spacing={2}>
            {recommendations.slice(0, 3).map((rec) => (
              <Grid item xs={12} sm={4} key={rec.question_id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}
                  onClick={() => navigate(`/questions/${rec.question_id}`)}
                >
                  <CardContent>
                    <Chip
                      label={rec.category_id}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(rec.category_id),
                        color: 'white',
                        mb: 1
                      }}
                    />
                    <Typography variant="body2" sx={{ minHeight: 40 }}>
                      {getDynamicQuestionTitle(rec)}
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                      {rec.is_free ? '免費' : formatPrice(rec.price)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* 確認對話框 */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>確認查詢</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            確定要查詢以下問題嗎？
          </Typography>

          <Alert severity="info" icon={<WarningIcon />}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>問題：</strong>{getDynamicQuestionTitle(question)}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              <strong>費用：</strong>{isQuestionFree(question.is_free) ? '免費' : formatPrice(question.price || 0)}
            </Typography>
            {user && !isQuestionFree(question.is_free) && (
              <Typography variant="subtitle2">
                <strong>查詢後餘額：</strong>{formatPrice(((user.bonus_points || 0) + (user.balance || 0)) - (question.price || 0))}
              </Typography>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={processing}>
            取消
          </Button>
          <Button onClick={handleQuery} variant="contained" disabled={processing}>
            {processing ? '處理中...' : '確認查詢'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuestionDetailPage;
